"use client";

import { sortBy } from "es-toolkit/array";
import { usePathname, useSearchParams } from "next/navigation";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  type CustomEventCommonPayload,
  type CustomEventInput,
  type CustomEventName,
  type CustomEventParameterValueFor,
  customEventToDataLayerEvent,
} from "@/lib/analytics/custom-events";
import { pageViewEventFromAnalyticsStaticData } from "./page-view-event";
import type { AnalyticsStaticData, DataLayerEvent } from "./types";

export type TrackEvent = <EventName extends CustomEventName>(
  input: CustomEventInput<EventName>,
) => boolean;

type ProductArea = CustomEventParameterValueFor<"product_area">;

/** 既存の page_view 実装と同じく、GTM が用意した dataLayer がある場合だけ page_view を送る。 */
function existingDataLayerQueue(): DataLayerEvent[] | null {
  if (typeof window === "undefined") {
    return null;
  }

  return window.dataLayer ?? null;
}

/** GTM が有効、または既存 queue がある client で custom event 用 dataLayer queue を返す。 */
function customEventDataLayerQueue(): DataLayerEvent[] | null {
  if (typeof window === "undefined") {
    return null;
  }
  if (!process.env.NEXT_PUBLIC_GTM_ID && !window.dataLayer) {
    return null;
  }

  window.dataLayer ??= [];
  return window.dataLayer;
}

function pushCustomEventToDataLayer<EventName extends CustomEventName>(
  input: CustomEventInput<EventName>,
  commonPayload: CustomEventCommonPayload = {},
): boolean {
  const dataLayer = customEventDataLayerQueue();
  if (!dataLayer) {
    return false;
  }

  try {
    dataLayer.push(customEventToDataLayerEvent(input, commonPayload));
    return true;
  } catch (error) {
    console.error("Failed to push custom analytics event", error);
    return false;
  }
}

const TrackEventContext = createContext<TrackEvent>((input) =>
  pushCustomEventToDataLayer(input),
);

/**
 * AnalyticsDataProvider が取得した page 文脈を使い、page_view 送信と custom event の共通 parameter 注入をまとめて扱う。
 * React Context で trackEvent を渡すため、module-level mutable state に依存せず component tree に沿って文脈が決まる。
 */
export function AnalyticsClientBoundary({
  analyticsStaticData,
  contentGroup,
  children = null,
  sendPageView = true,
}: {
  analyticsStaticData: AnalyticsStaticData;
  contentGroup: string;
  children?: ReactNode;
  sendPageView?: boolean;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastSentRef = useRef<string | null>(null);

  const locationKey = useMemo(() => {
    const basePath = pathname ?? "";

    const sortedEntries = sortBy(
      Array.from(searchParams.entries()).map(([key, value]) => ({
        key,
        value,
      })),
      ["key", "value"],
    ).map(({ key, value }) => [key, value]);

    if (sortedEntries.length === 0) {
      return basePath;
    }

    return `${basePath}?${new URLSearchParams(sortedEntries).toString()}`;
  }, [pathname, searchParams]);

  const pageViewEvent = useMemo(
    () =>
      pageViewEventFromAnalyticsStaticData({
        analyticsStaticData,
        contentGroup,
        pathname: pathname ?? "",
      }),
    [analyticsStaticData, contentGroup, pathname],
  );

  const commonPayload = useMemo(
    () =>
      customEventCommonPayloadFromAnalyticsStaticData(
        analyticsStaticData,
        contentGroup,
        pathname ?? "",
      ),
    [analyticsStaticData, contentGroup, pathname],
  );

  const trackEvent = useCallback<TrackEvent>(
    (input) => pushCustomEventToDataLayer(input, commonPayload),
    [commonPayload],
  );

  useEffect(() => {
    if (!sendPageView) {
      return;
    }

    // locationKey とイベント内容の組み合わせで二重送信を防止する。
    const signature = `${locationKey}__${JSON.stringify(pageViewEvent)}`;

    if (lastSentRef.current === signature) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const dataLayer = existingDataLayerQueue();
    if (!dataLayer) {
      return;
    }

    dataLayer.push(pageViewEvent);

    lastSentRef.current = signature;
  }, [pageViewEvent, locationKey, sendPageView]);

  return (
    <TrackEventContext.Provider value={trackEvent}>
      {children}
    </TrackEventContext.Provider>
  );
}

/**
 * custom event を送信する関数を返す。
 * AnalyticsClientBoundary 配下では page 文脈由来の共通 parameter を付与し、配下でない場合は event 固有 payload だけを送る。
 */
export function useTrackEvent(): TrackEvent {
  return useContext(TrackEventContext);
}

/** page_view 用 metadata から custom event に自動付与する共通分析軸を作る。 */
function customEventCommonPayloadFromAnalyticsStaticData(
  analyticsStaticData: AnalyticsStaticData,
  contentGroup: string,
  pathname: string,
): CustomEventCommonPayload {
  return {
    employees: analyticsStaticData.employeeStatus,
    product_area: productAreaForAnalyticsPath(pathname),
    content_group: contentGroup,
    plan: analyticsStaticData.orgData?.planName || "unknown",
    workspace_role:
      analyticsStaticData.workspaceRole?.toLowerCase() ?? "unknown",
  };
}

/** URL path から custom event の低カーディナリティな product area を推定する。 */
function productAreaForAnalyticsPath(pathname: string): ProductArea {
  if (pathname.includes("/sa/")) return "sa";
  if (pathname.includes("/ua/")) return "ua";
  if (pathname.includes("/iw/")) return "iw";
  if (pathname.includes("/dashboards/")) return "dashboard";
  if (pathname.includes("/ai")) return "ai";
  if (pathname.includes("/settings")) return "settings";
  if (pathname.includes("/new") || pathname.includes("/terms-agreement")) {
    return "workspace";
  }
  return "unknown";
}
